<template>
  <div @click="handlePlayerClick" class="preview-player-card"
    :class="{ captain: player.isCaptain, 'vice-captain': player.isViceCaptain, 'team-a': isTeamA }">
    <div v-if="player.isCaptain" class="role-indicator captain-badge">C</div>
    <div v-else-if="player.isViceCaptain" class="role-indicator vice-badge">VC</div>

    <div class="avatar-wrapper">
      <slot name="avatar" :player="player" :show-dream-team-badge="showDreamTeamBadge">
        <Avatar width="55px" height="55px" :src="player.avatar" class="player-avatar" />
        <div v-if="showDreamTeamBadge" class="dream-team-badge">
          <SvgIcon name="dt" class="dt-icon" />
        </div>
      </slot>
    </div>

    <div class="player-info">
      <div v-if="showStatusDots" class="status-dots-container">
        <div v-for="n in starting11Count" :key="'red-' + n" class="status-dot red-dot"></div>
        <div v-for="n in substituteCount" :key="'purple-' + n" class="status-dot purple-dot"></div>
      </div>
      <div class="player-name">{{ player.name }}</div>
      <div v-if="matchStatus === MatchStatus.NOT_STARTED" class="player-points">{{ player.rating || 0 }} Cr</div>
      <div v-else class="player-points">{{ player.points || 0 }} Pts</div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import Avatar from '@/components/Avatar/index.vue'
import SvgIcon from '@/components/SvgIcon/index.vue'
import { MatchStatus } from '@/views/contests/matchesType.js'

const props = defineProps({
  player: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  matchInfo: {
    type: Object,
    required: true,
    default: () => ({}),
  },
})

const emit = defineEmits(['click'])

const isTeamA = computed(() => props?.matchInfo?.teamaId === props.player.teamId)
const matchStatus = computed(() => props?.matchInfo?.status || 1)
const showStatusDots = computed(() => {
  return matchStatus.value !== MatchStatus.NOT_STARTED &&
    (props.player.starting11 > 0 || props.player.substitute > 0)
})
const starting11Count = computed(() => props.player.starting11 || 0)
const substituteCount = computed(() => props.player.substitute || 0)
const showDreamTeamBadge = computed(() => {
  return props?.matchInfo?.status === MatchStatus.COMPLETED && props.player.dreamTeam === 1
})

const handlePlayerClick = () => {
  emit('click', props.player)
}
</script>

<style lang="less" scoped>
.preview-player-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 80px;

  &.team-a {
    .player-name {
      color: #333 !important;
      background: #fff !important;
    }
  }

  .role-indicator {
    position: absolute;
    top: -4px;
    left: 0%;
    transform: translateX(-50%);
    width: 20px;
    height: 20px;
    border-radius: 50%;
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    border: 1px solid #DADADA;

    &.captain-badge,
    &.vice-badge {
      background: #202B3C;
      color: #DADADA;
    }
  }

  .status-dots-container {
    position: absolute;
    top: -2px;
    left: 0%;
    display: flex;
    gap: 2px;
    z-index: 3;

    .status-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      border: 1px solid #ffffff;

      &.red-dot {
        background: #ff4d4f;
      }

      &.purple-dot {
        background: #722ed1;
      }
    }
  }

  .avatar-wrapper {
    position: relative;
    display: inline-block;

    .dream-team-badge {
      position: absolute;
      bottom: -4px;
      right: -4px;
      width: 18px;
      height: 18px;
      z-index: 3;
      background: transparent;

      .dt-icon {
        width: 100%;
        height: 100%;
      }
    }
  }

  .player-info {
    text-align: center;
    margin-top: 2px;
    position: relative;

    .player-name {
      font-size: 11px;
      color: #fff;
      background: rgba(0, 0, 0, 0.5);
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      border-radius: 4px;
      padding: 2px 6px;
      text-overflow: ellipsis;
      max-width: 70px;
      position: relative;
    }

    .player-points {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.9);
      margin-top: 2px;
    }
  }
}
</style>
